'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Background } from '@/components/home/DarkstarBackground'
import { Shield, Lock, Eye, Share2, Bell, Clock, Users, RefreshCw, Mail, Globe } from 'lucide-react'

export default function PrivacyPolicy() {
  return (
    <>
      <Background />
      <div className="relative min-h-screen py-24 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="max-w-4xl mx-auto"
        >
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-xl tracking-tight text-primary mb-4">Privacy Policy</h1>
            <p className="text-lg text-primary/70">Last updated: {new Date().toLocaleDateString()}</p>
          </motion.div>

          <div className="space-y-8">
            {/* Information We Collect */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-primary/80">
                <h3 className="text-sm text-primary">Account Information</h3>
                <p>
                  We collect personal information that you voluntarily provide when you create or update your account. This may include your name, contact details, profile picture, authentication credentials, account preferences, and any other information you choose to provide. This information is essential for providing our services and personalizing your experience.
                </p>

                <h3 className="text-sm text-primary">Reading Data</h3>
                <p>
                  To enhance your experience with our tarot reading services, we collect data related to your tarot reading history, card interpretations, conversation logs with our AI, and your reading preferences. This allows us to deliver more accurate, relevant, and personalized readings tailored to your interests.
                </p>

                <h3 className="text-sm text-primary">Usage Information</h3>
                <p>
                  We automatically collect information about how you interact with our services, including token consumption, feature usage statistics, device information, browser type, IP address, and session data. This helps us understand user engagement, improve our platform’s functionality and performance, and detect and prevent fraud or misuse.
                </p>
              </div>
            </section>

            {/* How We Protect Your Data */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">How We Protect Your Data</h2>
              </div>
              <p className="text-primary/80">
                We are committed to safeguarding your personal information. We use industry-standard security measures, including encryption technologies such as SSL/TLS, to protect data transmitted to and from our platform. Our servers are secured with firewalls and regularly monitored for potential vulnerabilities. Access to personal data is restricted to authorized personnel who are bound by confidentiality obligations. We periodically review and update our security practices to address new threats and follow best practices for data protection.
              </p>
            </section>

            {/* Legal Bases for Processing (if applicable) */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Legal Bases for Processing Your Data</h2>
              </div>
              <p className="text-primary/80">
                Depending on your location, we may process your personal information under certain legal bases. For example, if you are located in the European Economic Area (EEA), the United Kingdom, or other jurisdictions that have data protection laws, we process your data where:
              </p>
              <ul className="list-disc list-inside text-primary/80 mt-4">
                <li>It is necessary to provide our services to you or perform a contract.</li>
                <li>It is necessary for our legitimate interests, such as improving our platform, ensuring security, and preventing fraud, provided those interests are not overridden by your fundamental rights.</li>
                <li>You have given us your consent, where required, which you can withdraw at any time.</li>
                <li>We need to comply with a legal obligation.</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Your Rights</h2>
              </div>
              <p className="text-primary/80">
                You have certain rights regarding your personal data, which may vary depending on your jurisdiction. These rights may include:
              </p>
              <ul className="list-disc list-inside text-primary/80 mt-4">
                <li>The right to access the personal information we hold about you and request its correction, update, or deletion.</li>
                <li>The right to export your reading history or other data for personal use.</li>
                <li>The right to object to, or request that we limit, the processing of your personal data.</li>
                <li>The right to withdraw your consent to processing where we relied on your consent.</li>
                <li>The right to lodge a complaint with a supervisory authority if you believe your privacy rights have been violated.</li>
              </ul>
              <p className="mt-4 text-primary/80">
                You can adjust your preferences in your account settings or contact us directly to exercise these rights. We will respond to requests in accordance with applicable data protection laws.
              </p>
            </section>

            {/* Data Sharing and Disclosure */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Share2 className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Data Sharing and Disclosure</h2>
              </div>
              <p className="text-primary/80">
                We do not sell or rent your personal information to third parties. We may share your data with trusted service providers who assist us in operating our website, delivering services, analyzing data, or conducting business activities, provided that they agree to keep this information confidential and comply with data protection laws.
              </p>
              <p className="mt-4 text-primary/80">
                We may also disclose your information when required by law, such as responding to a subpoena, legal order, or government request, or to protect our rights, property, and safety or that of others. Additionally, we may share aggregated, anonymized data that does not identify you personally for research, analytical, or marketing purposes.
              </p>
            </section>

            {/* International Data Transfers */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">International Data Transfers</h2>
              </div>
              <p className="text-primary/80">
                If you are located outside the country where our servers or service providers are located, we may transfer your personal information across international borders. When we do so, we take appropriate measures to ensure that your data is protected in accordance with this Privacy Policy and applicable data protection laws. These measures may include using standard contractual clauses or relying on an adequacy decision from the relevant regulatory authority.
              </p>
            </section>

            {/* Cookies and Tracking Technologies */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Bell className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Cookies and Tracking Technologies</h2>
              </div>
              <p className="text-primary/80">
                We use cookies and similar tracking technologies to enhance your user experience. Cookies are small data files stored on your device that help us remember your preferences, understand usage patterns, and personalize content. We may also use third-party analytics tools that employ cookies and other tracking technologies to help us analyze and improve our services.
              </p>
              <p className="mt-4 text-primary/80">
                You can control the use of cookies at the individual browser level. If you choose to disable cookies, some features or functionalities of our platform may not operate correctly. For more information on how we use cookies and how you can manage them, please review your browser's help documentation.
              </p>
            </section>

            {/* Data Retention */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Data Retention</h2>
              </div>
              <p className="text-primary/80">
                We retain personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your personal data, we will securely delete, anonymize, or aggregate it. If you close your account, we will delete your personal information within a reasonable timeframe, except where we are required or permitted by law to retain certain data for legal, regulatory, or compliance purposes.
              </p>
            </section>

            {/* Children&apos;s Privacy */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Users className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Children&apos;s Privacy</h2>
              </div>
              <p className="text-primary/80">
                Our services are not intended for individuals under the age of 13, and we do not knowingly collect or solicit personal information from children under 13. If we become aware that we have collected personal data from a child under 13, we will promptly take steps to delete that information. If you believe we may have information about a child under 13, please contact us immediately.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Changes to This Policy</h2>
              </div>
              <p className="text-primary/80">
                We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or for other operational reasons. When we make significant changes, we will notify you via email or by posting a prominent notice on our website before the changes take effect. Your continued use of our services after the effective date of the updated Privacy Policy constitutes your acceptance of the changes.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-white/10 backdrop-blur-sm rounded-sm p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-primary" />
                <h2 className="text-md text-primary">Contact Us</h2>
              </div>
              <p className="text-primary/80">
                If you have any questions, concerns, or comments regarding this Privacy Policy or our data practices, please contact us at&nbsp;
                <a href="mailto:privacy@arcanavibes.com" className="text-primary underline">
                  privacy@arcanavibes.com
                </a>. We are committed to addressing your inquiries and will respond as soon as reasonably possible.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </>
  )
}